import { useEffect, useRef, useState } from 'react';
import type { SalesFunnelStage } from '@/types/sales-performance.types';
import { count, percent } from './format';

const FLOW_HEIGHT = 280;
const DROP_GAP = 22;
const BAR_WIDTH = 14;
const LABEL_SEPARATION = 40;
const STAGE_COLORS = ['indigo-600', 'indigo-500', 'indigo-400', 'indigo-300', 'indigo-300'];

interface PerformanceFunnelProps {
  stages: SalesFunnelStage[];
  title: string;
  orders?: number;
  /** The conversation design labels buying intent inside the first flow; Sales chats remain its input. */
  conversationEntry?: boolean;
}

export function PerformanceFunnel({ stages, title, orders, conversationEntry = false }: PerformanceFunnelProps) {
  const [view, setView] = useState('funnel');
  const container = useRef<HTMLDivElement>(null);
  const [graphWidth, setGraphWidth] = useState(1152);
  useEffect(() => {
    const element = container.current;
    if (!element) return;
    const measure = () => setGraphWidth(Math.max(760, element.clientWidth));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [view]);
  const maximum = stages[0]?.count || 1;
  const biggestDrop = Math.max(0, ...stages.map((stage) => stage.dropOff));
  const columns = conversationEntry ? stages.slice(1) : stages;
  const columnWidth = graphWidth / Math.max(1, columns.length);
  const height = (value: number) => Math.max(0, value / maximum * FLOW_HEIGHT);
  const retainedLabelY = (value: number) => Math.max(18, height(value) / 2 - 2);
  // Drop-offs are labelled in their destination column. Reserve both lines of
  // its retained label and keep the text inside the chart, leaving flows to scale.
  const dropLabelYs = columns.map((stage, index) => Math.min(FLOW_HEIGHT, Math.max(
    height(stage.count) + DROP_GAP + height(stage.dropOff) / 2 - 3,
    retainedLabelY(columns[index + 1]?.count ?? stage.count) + LABEL_SEPARATION,
  )));
  const lastColumn = columns.length - 1;
  // The final column also holds the final step's own drop-off, so these two
  // drop-off annotations need separate slots below the retained label.
  if (lastColumn > 0 && columns[lastColumn - 1].dropOff > 0 && columns[lastColumn].dropOff > 0) {
    dropLabelYs[lastColumn - 1] = Math.min(dropLabelYs[lastColumn - 1], FLOW_HEIGHT - LABEL_SEPARATION);
    dropLabelYs[lastColumn] = Math.max(dropLabelYs[lastColumn], dropLabelYs[lastColumn - 1] + LABEL_SEPARATION);
  }

  return <section aria-label={title}>
    <div className='mb-4 flex h-7 items-center justify-between'>
      <h2 className='text-xs font-normal text-gray-800'>{title}</h2>
      <select aria-label={`${title} chart type`} value={view} onChange={(event) => setView(event.target.value)} className='h-7 rounded-full border border-gray-500 bg-white py-0.5 pr-2 pl-3 text-xs text-ink'>
        <option value='funnel'>Funnel graph</option><option value='bar'>Bar graph</option>
      </select>
    </div>
    <div className='rounded-3xl border border-gray-500 p-6'>
      <div className='mb-6 flex flex-wrap gap-4 text-xs leading-4 text-gray-900'>
        <span className='flex items-center gap-1.5'><i className='h-2.5 w-[17px] rounded-full bg-indigo-500' />Moved to next step</span>
        <span className='flex items-center gap-1.5'><i className='h-2.5 w-[17px] rounded-full bg-gray-500' />Dropped off</span>
        <span className='flex items-center gap-1.5'><i className='h-2.5 w-[17px] rounded-full bg-red-500' />Biggest dropped off</span>
      </div>
      {view === 'funnel' ? <div ref={container} className='overflow-x-auto'>
        <svg viewBox={`0 0 ${graphWidth} 308`} role='img' aria-label={`${title}: ${stages.map((stage) => `${stage.label} ${count(stage.count)}`).join(', ')}`} className='block h-[308px] min-w-[760px] w-full'>
          {columns.map((stage, index) => {
            const previous = index ? columns[index - 1] : stages[0];
            const x = index * columnWidth;
            const endX = index === columns.length - 1 ? graphWidth - BAR_WIDTH : (index + 1) * columnWidth;
            const previousHeight = height(previous?.count ?? 0);
            const mainHeight = height(stage.count);
            const droppedHeight = height(stage.dropOff);
            const dropY = mainHeight + DROP_GAP;
            const labelY = retainedLabelY(stage.count);
            const dropColor = stage.dropOff === biggestDrop ? 'red-500' : 'gray-500';
            const controlX = x + columnWidth / 2;
            const labelX = x + BAR_WIDTH + 12;
            // The first node is the full cohort; each band shows the next retained outcome.
            return <g key={stage.key}>
              <path d={`M ${x + BAR_WIDTH} 0 H ${endX + BAR_WIDTH} V ${mainHeight} H ${x + BAR_WIDTH} Z`} fill='var(--color-indigo-100)' opacity={Math.max(.28, 1 - index * .23)} />
              {stage.dropOff > 0 && <>
                <path d={`M ${x + BAR_WIDTH} ${mainHeight} C ${controlX} ${mainHeight}, ${controlX} ${dropY}, ${endX} ${dropY} V ${dropY + droppedHeight} C ${controlX} ${dropY + droppedHeight}, ${controlX} ${previousHeight}, ${x + BAR_WIDTH} ${previousHeight} Z`} fill={`var(--color-${dropColor})`} opacity='.16' />
                <rect x={endX} y={dropY} width={BAR_WIDTH} height={droppedHeight} rx={3} fill={`var(--color-${dropColor})`} />
                <text x={index === columns.length - 1 ? labelX : endX + BAR_WIDTH + 10} y={dropLabelYs[index]} fill='var(--color-ink)' fontSize={12} fontWeight={600}>
                  {count(stage.dropOff)}<tspan fill='var(--color-gray-900)' fontSize={10} fontWeight={400}> / {percent(stage.dropOff / maximum * 100)}</tspan>
                  <tspan x={index === columns.length - 1 ? labelX : endX + BAR_WIDTH + 10} dy={17} fill='var(--color-gray-900)' fontSize={11} fontWeight={400}>{stage.dropOffLabel}</tspan>
                </text>
              </>}
              <rect x={x} y={0} width={BAR_WIDTH} height={previousHeight} rx={3} fill={`var(--color-${STAGE_COLORS[index] ?? 'indigo-300'})`} />
              <text x={labelX} y={labelY} fill='var(--color-ink)' fontSize={12} fontWeight={600}>
                {count(stage.count)}<tspan fill='var(--color-gray-900)' fontSize={10} fontWeight={400}> / {percent(stage.count / maximum * 100)}</tspan>
                <tspan x={labelX} dy={17} fill='var(--color-gray-900)' fontSize={11} fontWeight={400}>{stage.label}</tspan>
              </text>
            </g>;
          })}
        </svg>
      </div> : <div className='space-y-5 py-2'>
        {stages.map((stage, index) => <div key={stage.key} className='grid grid-cols-[130px_1fr] items-center gap-5'>
          <div className='text-xs text-ink'>{stage.label}<div className='mt-1 text-[11px] text-gray-800'>{count(stage.count)} / {percent(stage.count / maximum * 100)}</div></div>
          <div className='flex h-3 overflow-hidden rounded-full bg-gray-100' aria-label={`${stage.label}: ${stage.count}; ${stage.dropOffLabel ?? 'drop-off'}: ${stage.dropOff}`}>
            <div className='h-full rounded-full bg-indigo-500' style={{ width: `${stage.count / maximum * 100}%` }} />
            {index > 0 && <div className={stage.dropOff === biggestDrop ? 'bg-red-200' : 'bg-gray-500'} style={{ width: `${stage.dropOff / maximum * 100}%` }} />}
          </div>
        </div>)}
      </div>}
      {orders !== undefined && <p className='mt-3 text-xs text-gray-800'>{count(stages.at(-1)?.count ?? 0)} ordering conversations · {count(orders)} orders</p>}
      <details className='sr-only focus-within:not-sr-only focus-within:mt-3 focus-within:text-xs focus-within:text-gray-800'>
        <summary className='w-fit cursor-pointer text-[11px] text-gray-700'>View counts and drop-offs</summary>
        <table className='mt-2 w-full text-left'><thead><tr><th>Step</th><th>Conversations</th><th>Drop-off from previous step</th></tr></thead>
          <tbody>{stages.map((stage) => <tr key={stage.key}><td className='py-1'>{stage.label}</td><td>{count(stage.count)}</td><td>{stage.dropOffLabel ? `${stage.dropOffLabel}: ${count(stage.dropOff)}` : '—'}</td></tr>)}</tbody>
        </table>
      </details>
    </div>
  </section>;
}
